// src/games/HuntWords/api.ts
import { apiNode } from '../../services/api';
import { RANKING_TIPO, createRankingPosition } from '../core/api';
import type { FinishMemo } from '../core/finish/finishMemo';
import { throwIfRejected } from '../core/finish/translateFinishError';
import { finishTempo } from '../core/session/time';
import type { FinishResult } from '../core/session/useGameSession';
import type { IHuntWordRes, ISolution } from './huntWordInterface';
import { WordSearchCache, buildSolutions, buildWordSearchCache, readWordSearchCache, wordsearchPoints } from './rules';

export interface WordSearchProgressRow {
  cache: unknown;
  finished: boolean | number;
  pontuacao: number | null;
  tempo: number | null;
}

export interface SaveWordSearchArgs {
  userId: number;
  jogoId: number;
  cache: WordSearchCache;
  finished: boolean;
  seconds: number;
}

export interface WordSearchLoad {
  game: IHuntWordRes;
  solutions: ISolution[];
  found: ISolution[];
  correctCells: number[];
  savedSeconds: number;
  finished: FinishResult | null;
}

export async function getWordSearch(jogoId: number): Promise<IHuntWordRes | null> {
  const { data } = await apiNode.get(`/show_wordsearch/${jogoId}`);
  if (!data || !data.game) return null;
  return typeof data.game === 'string' ? { ...data, game: JSON.parse(data.game) } : data;
}

export async function getWordSearchProgress(userId: number, jogoId: number): Promise<WordSearchProgressRow | null> {
  const { data } = await apiNode.get(`/wordsearch_progresso/${userId}`, { params: { jogos_id: jogoId } });
  return Array.isArray(data) && data[0] ? data[0] : null;
}

export async function saveWordSearchProgress({ userId, jogoId, cache, finished, seconds }: SaveWordSearchArgs): Promise<{ action?: boolean }> {
  const { data } = await apiNode.post('/wordsearch_progresso', {
    users_id: userId,
    jogos_id: jogoId,
    cache: buildWordSearchCache(cache.found, cache.correctCells),
    finished,
    pontuacao: wordsearchPoints(cache.found.length),
    tempo: Math.max(0, Math.round(seconds)),
  });
  return data ?? {};
}

/**
 * Fim: progresso finished:true e depois o ranking tipo 7. Com o mesmo `memo`, um reenvio
 * pula o progresso que já foi gravado e manda só o ranking.
 */
export async function finishWordSearch(args: Omit<SaveWordSearchArgs, 'finished'>, memo: FinishMemo = {}): Promise<void> {
  const tempo = (memo.tempo ??= finishTempo(args.seconds));
  if (!memo.progressSaved) {
    throwIfRejected(await saveWordSearchProgress({ ...args, finished: true, seconds: tempo }));
    memo.progressSaved = true;
  }
  await createRankingPosition({
    id_jogo: args.jogoId,
    id_user: args.userId,
    id_tipo: RANKING_TIPO.wordsearch,
    pontuacao: wordsearchPoints(args.cache.found.length),
    tempo,
  });
}

export function wordSearchResult(all: ISolution[], found: ISolution[]): FinishResult {
  return {
    points: wordsearchPoints(found.length),
    maxPoints: wordsearchPoints(all.length),
    correct: found.length,
    total: all.length,
  };
}

/** `null` = jogo sem conteúdo. Erro ao ler o progresso SOBE (não abre em branco). */
export async function loadWordSearchGame(jogoId: number, userId?: number): Promise<WordSearchLoad | null> {
  const [game, progress] = await Promise.all([
    getWordSearch(jogoId),
    userId !== undefined ? getWordSearchProgress(userId, jogoId) : Promise.resolve(null),
  ]);
  if (!game) return null;
  const puzzle = game.game['crossword-compiler']['rectangular-puzzle']['word-search'];
  const solutions = buildSolutions(puzzle.word, game.texto);
  const restored = progress ? readWordSearchCache(progress.cache, solutions) : { found: [], correctCells: [] };
  return {
    game,
    solutions,
    found: restored.found,
    correctCells: restored.correctCells,
    savedSeconds: Math.max(0, Math.round(progress?.tempo ?? 0)),
    finished: progress?.finished ? wordSearchResult(solutions, restored.found) : null,
  };
}
